import React, { useState, useEffect } from "react";
import { Button, Grid, FormControl, InputLabel, Select, MenuItem, Menu, TextField, FormControlLabel, Checkbox, InputAdornment, LinearProgress, Box, FormHelperText, Tooltip } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { Info as InfoIcon } from "@material-ui/icons";
import { Club } from "api/clubs";
import { patchClubs, postClubsCheckinQrcode } from "api/admin/clubs";
import i18n from "./i18n";

// prop types
interface BookingsTabProps {
    club?: Club,
    onFetchUserProfile: () => void,
}

// bookings club settings tab
const BookingsTab = ({ club, onFetchUserProfile }: BookingsTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [bookingMinDurationMins, setBookingMinDurationMins] = useState(0);
    const [comingUpBookingMins, setComingUpBookingMins] = useState(0);
    const [geofencingRadiusMeters, setGeofencingRadiusMeters] = useState(0);
    const [internalBookingAtomMins, setInternalBookingAtomMins] = useState(0);
    const [isCoronaReady, setIsCoronaReady] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [locationRestrictionType, setLocationRestrictionType] = useState<Club["locationRestrictionType"]>("none");
    const [prohibitStandardBookings, setProhibitStandardBookings] = useState(false);
    const [restrictAllBookingsToOneHour, setRestrictAllBookingsToOneHour] = useState(false);
    const [showCreateQrCodeMenu, setShowCreateQrCodeMenu] = useState<HTMLButtonElement | null>(null);

    // save action
    const saveAction = async () => {
        setLoading(true);
        try {
            await patchClubs({
                bookingMinDurationMins,
                clubIds: [club!.id],
                comingUpBookingMins,
                geofencingRadiusMeters,
                internalBookingAtomMins,
                isCoronaReady,
                locationRestrictionType,
                prohibitStandardBookings,
                restrictAllBookingsToOneHour,
            });
            onFetchUserProfile(); // refresh clubs
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // create qr code action
    const createQrCodeAction = async (theme: "dark" | "light") => {
        setShowCreateQrCodeMenu(null);
        setLoading(true);
        try {
            const { url } = await postClubsCheckinQrcode({ clubId: club!.id, theme });
            window.open(url);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // update fields
    useEffect(() => {
        if (club) {
            setBookingMinDurationMins(club.bookingMinDurationMins);
            setComingUpBookingMins(club.comingUpBookingMins);
            setGeofencingRadiusMeters(club.geofencingRadiusMeters);
            setInternalBookingAtomMins(club.internalBookingAtomMins);
            setIsCoronaReady(club.capabilities.includes("corona-ready"));
            setLocationRestrictionType(club.locationRestrictionType);
            setProhibitStandardBookings(club.prohibitStandardBookings);
            setRestrictAllBookingsToOneHour(club.restrictAllBookingsToOneHour);
        }
    }, [club]);

    // render
    return (
        <Box style={{ alignItems: "center", display: "flex", flexDirection: "column", padding: "16px" }}>
            <Grid container spacing={2}>

                {/* booking atom */}
                <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{i18n.bookingAtom}</InputLabel>
                        <Select
                            label={i18n.bookingAtom}
                            value={internalBookingAtomMins.toString()}
                            onChange={(e) => setInternalBookingAtomMins(parseInt(e.target.value as string, 10))}
                        >
                            <MenuItem value="15">{`15 ${i18n.minutes} (6:00, 6:15, 6:30, 6:45)`}</MenuItem>
                            <MenuItem value="30">{`30 ${i18n.minutes} (6:00, 6:30, 7:00, 7:30)`}</MenuItem>
                            <MenuItem value="60">{`60 ${i18n.minutes} (6:00, 7:00, 8:00, 9:00)`}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* min duration */}
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                        inputProps={{ min: 15, step: 15 }} // eslint-disable-line
                        label={i18n.minDuration}
                        placeholder="15"
                        type="number"
                        value={bookingMinDurationMins}
                        variant="outlined"
                        onBlur={() => (Number.isNaN(bookingMinDurationMins) || bookingMinDurationMins < 15) && setBookingMinDurationMins(15)}
                        onChange={(e) => setBookingMinDurationMins(parseInt(e.target.value, 10))}
                    />
                </Grid>

                {/* coming up */}
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                        inputProps={{ min: 15, step: 15 }} // eslint-disable-line
                        label={i18n.maxComingUp}
                        placeholder="15"
                        type="number"
                        value={comingUpBookingMins}
                        variant="outlined"
                        onBlur={() => (Number.isNaN(comingUpBookingMins) || comingUpBookingMins < 15) && setComingUpBookingMins(15)}
                        onChange={(e) => setComingUpBookingMins(parseInt(e.target.value, 10))}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Tooltip title={i18n.comingUpBookingMinsDesc} arrow>
                        <InfoIcon color="secondary" fontSize="small" style={{ margin: "18px 0" }} />
                    </Tooltip>
                </Grid>

                {/* limit to 1 hour */}
                <Grid item xs={12}>
                    <FormControl>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={restrictAllBookingsToOneHour}
                                    color="primary"
                                    onChange={(e) => setRestrictAllBookingsToOneHour(e.target.checked)}
                                />
                            )}
                            label={i18n.limitBookingsToOneHour}
                        />
                    </FormControl>
                </Grid>

                {/* prohibit bookings */}
                <Grid item xs={12}>
                    <FormControl>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={prohibitStandardBookings}
                                    color="primary"
                                    onChange={(e) => setProhibitStandardBookings(e.target.checked)}
                                />
                            )}
                            label={i18n.prohibitStandardBookings}
                        />
                    </FormControl>
                </Grid>

                {/* corona ready */}
                <Grid item xs={6}>
                    <FormControl>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={isCoronaReady}
                                    color="primary"
                                    onChange={(e) => setIsCoronaReady(e.target.checked)}
                                />
                            )}
                            label={i18n.coronaReady}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    {club?.capabilities.includes("corona-ready") && (
                        <Button disabled={isLoading} variant="contained" onClick={(e) => setShowCreateQrCodeMenu(e.currentTarget)}>
                            {i18n.createQrCode}
                        </Button>
                    )}
                </Grid>
                {/* qr code menu */}
                <Menu anchorEl={showCreateQrCodeMenu} open={!!showCreateQrCodeMenu} onClose={() => setShowCreateQrCodeMenu(null)}>
                    <MenuItem onClick={() => createQrCodeAction("light")}>{i18n.light}</MenuItem>
                    <MenuItem onClick={() => createQrCodeAction("dark")}>{i18n.dark}</MenuItem>
                </Menu>

                {/* location restriction */}
                <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{i18n.locationRestriction}</InputLabel>
                        <Select
                            label={i18n.locationRestriction}
                            value={locationRestrictionType}
                            onChange={(e) => setLocationRestrictionType(e.target.value as typeof locationRestrictionType)}
                        >
                            <MenuItem value="none">{i18n.none}</MenuItem>
                            {locationRestrictionType === "check-in" && <MenuItem value="check-in">{i18n.checkInRequired}</MenuItem>}
                            <MenuItem value="geo-fence">{i18n.geofence}</MenuItem>
                        </Select>
                        <FormHelperText>{i18n.locationRestrictionDesc[locationRestrictionType]}</FormHelperText>
                    </FormControl>
                </Grid>

                {/* geofence radius */}
                <Grid item xs={6}>
                    <TextField
                        disabled={locationRestrictionType !== "geo-fence"}
                        fullWidth
                        InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                        inputProps={{ min: 50, step: 50 }} // eslint-disable-line
                        label={i18n.geofenceRadius}
                        placeholder="400"
                        type="number"
                        value={geofencingRadiusMeters}
                        variant="outlined"
                        onBlur={() => (Number.isNaN(geofencingRadiusMeters) || geofencingRadiusMeters < 0) && setGeofencingRadiusMeters(400)}
                        onChange={(e) => setGeofencingRadiusMeters(parseInt(e.target.value, 10))}
                    />
                </Grid>

                <Grid item xs={12}>
                    {isLoading && <LinearProgress />}
                </Grid>
            </Grid>

            {/* save button */}
            <Button
                color="primary"
                disabled={isLoading}
                style={{ margin: "16px" }}
                variant="contained"
                onClick={saveAction}
            >
                {i18n.save}
            </Button>
        </Box>
    );
};

export default BookingsTab;
